import { WireErrorSchema, type WireError } from '@immo/api-contract';
import {
  ApiError,
  BusinessRuleError,
  ForbiddenError,
  NetworkError,
  NotFoundError,
  ServerError,
  UnauthorizedError,
  UnknownApiError,
  ValidationError,
} from './api-error';

/**
 * The single place the wire error body is decoded, per ADR 0014. Every
 * integration layer routes failures through here so the contract dependency
 * lives at one import.
 *
 * - No `Response` (fetch threw / offline / aborted) → `NetworkError`.
 * - Body fails `WireErrorSchema.parse` → `UnknownApiError` (client-minted id).
 * - Otherwise → the class for the HTTP status, carrying the wire body.
 */
export async function parseApiError(input: Response | unknown): Promise<ApiError> {
  if (!(input instanceof Response)) {
    return new NetworkError({ cause: input });
  }

  if (input.ok) {
    throw new Error('parseApiError called on a successful response');
  }

  const status = input.status;

  let body: WireError;

  try {
    body = WireErrorSchema.parse(await input.json());
  } catch {
    return new UnknownApiError({ status });
  }

  return apiErrorFromStatus(status, body.error);
}

/**
 * Maps an HTTP status + already-parsed wire error body onto a typed
 * `ApiError`. Shared by `parseApiError` and any integration layer that
 * already holds a decoded `{ status, body }` (e.g. the ts-rest client).
 */
export function apiErrorFromStatus(status: number, error: WireError['error']): ApiError {
  switch (status) {
    case 400:
      return new ValidationError(error);
    case 401:
      return new UnauthorizedError(error);
    case 403:
      return new ForbiddenError(error);
    case 404:
      return new NotFoundError(error);
    case 422:
      return new BusinessRuleError(error);
    default:
      return new ServerError(status, error);
  }
}
